import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import {
  FileDown,
  TrendingUp,
  CheckCircle,
  Database,
  Brain,
  MapPin,
  Download,
  Target,
  BarChart3,
} from "lucide-react";
import "./executive-kpi-styles.css";
import MaritimeLayout from "../common/Applayout";

export default function ExecutiveKPIDashboard() {
  const kpiData = {
    totalRevenue: 8810000000,
    imoCompliance: 100,
    activeEmirates: 7,
    dataSyncRate: 99.2,
    systemUptime: 99.95,
  };

  const revenueTrend = [
    { month: "Jan", value: 500 },
    { month: "Feb", value: 600 },
    { month: "Mar", value: 720 },
    { month: "Apr", value: 760 },
    { month: "May", value: 800 },
    { month: "Jun", value: 850 },
    { month: "Jul", value: 860 },
    { month: "Aug", value: 870 },
    { month: "Sep", value: 875 },
    { month: "Oct", value: 878 },
    { month: "Nov", value: 880 },
  ];

  const revenueByPort = [
    { port: "Jebel Ali", value: 4837 },
    { port: "Khalifa", value: 1533 },
    { port: "Sharjah", value: 520 },
    { port: "Fujairah", value: 200 },
    { port: "RAK", value: 135 },
    { port: "Ajman", value: 90 },
    { port: "UAQ", value: 85 },
  ];

  const distributionData = [
    { name: "CEBS", value: 25, color: "#1d4ed8" },
    { name: "MOEI", value: 25, color: "#f59e0b" },
    { name: "Ports", value: 50, color: "#10b981" },
  ];

  const aiInsights = [
    "📈 Vessel traffic expected to increase by 4.3% next month due to winter trade season.",
    "💰 Port dues projected to rise by AED +35M month-over-month.",
    "⚙️ Average vessel turnaround time improved by 1.8 hours vs last month.",
    "🚨 Slight delay in FAL submissions detected in Sharjah (+2%), flagged for monitoring.",
    "🔍 AI recommends optimizing pilotage scheduling during peak morning windows.",
  ];

  const emirateStatus = [
    { name: "Abu Dhabi", sync: "100% Data Sync", color: "#10b981" },
    { name: "Dubai", sync: "99.8% Data Sync", color: "#10b981" },
    { name: "Sharjah", sync: "99.5% Data Sync", color: "#10b981" },
    { name: "Fujairah", sync: "98.9% Data Sync", color: "#10b981" },
    { name: "RAK", sync: "98.2% Data Sync", color: "#10b981" },
    { name: "Ajman", sync: "97.8% Data Sync", color: "#10b981" },
    { name: "UAQ", sync: "97.4% Data Sync", color: "#10b981" },
  ];

  const systemMetrics = [
    { metric: "Average API latency", value: "230ms" },
    { metric: "Data throughput", value: "3.2 GB/day" },
    { metric: "Error rate", value: "0.04%" },
    { metric: "System uptime", value: "99.95%" },
    { metric: "Data replication lag", value: "< 10s" },
  ];

  const exportData = (format) => {
    if (format === "csv") alert("Executive KPI Data exported as CSV");
    else if (format === "pdf") alert("Executive Report exported as PDF");
  };

  // Custom Card Components
  const ExecutiveCard = ({ children, className = "", hover = false }) => (
    <div
      className={`executive-kpi-card ${
        hover ? "cursor-pointer" : ""
      } ${className}`}
    >
      {children}
    </div>
  );

  const ExecutiveCardHeader = ({ children, className = "" }) => (
    <div className={`executive-kpi-card-header ${className}`}>{children}</div>
  );

  const ExecutiveCardTitle = ({ children, className = "" }) => (
    <h3 className={`executive-kpi-card-title ${className}`}>{children}</h3>
  );

  const ExecutiveCardContent = ({ children, className = "" }) => (
    <div className={`executive-kpi-card-content ${className}`}>{children}</div>
  );

  return (
    <MaritimeLayout>
      <div className="executive-kpi-dashboard">
        {/* Header */}
        <div className="executive-kpi-header">
          <div className="executive-flex executive-items-center executive-justify-between executive-mb-4">
            <div>
              <h1 className="executive-kpi-gradient-title">
                🇦🇪 National Maritime Center – Executive KPI Dashboard
              </h1>
              <p className="executive-text-slate-600 executive-text-lg">
                Real-time performance monitoring across UAE maritime operations
              </p>
            </div>
            <div className="executive-flex executive-space-x-3">
              <button
                onClick={() => exportData("csv")}
                className="executive-kpi-btn executive-btn-success"
              >
                <Download className="executive-mr-2" size={18} /> Export CSV
              </button>
              <button
                onClick={() => exportData("pdf")}
                className="executive-kpi-btn executive-btn-primary"
              >
                <FileDown className="executive-mr-2" size={18} /> Export PDF
              </button>
            </div>
          </div>

          <p className="executive-text-slate-600 executive-leading-relaxed executive-bg-blue-50 executive-rounded-xl executive-p-4 executive-border executive-border-blue-100">
            This executive dashboard consolidates performance across all UAE
            ports and maritime systems under MOEI supervision. It provides
            real-time data on IMO FAL compliance, system uptime, financial
            performance, and predictive AI insights to support strategic
            decisions.
          </p>
        </div>

        {/* KPI Cards */}
        <div className="executive-kpi-grid">
          <div className="executive-kpi-item">
            <h3 className="executive-kpi-label">Total Revenue</h3>
            <div className="executive-kpi-value executive-text-blue-700">
              AED {(kpiData.totalRevenue / 1e9).toFixed(2)}B
            </div>
            <p className="executive-text-sm executive-text-slate-600">
              Cumulative revenue generated by all UAE maritime operations YTD.
            </p>
          </div>

          <div className="executive-kpi-item">
            <h3 className="executive-kpi-label">IMO FAL Compliance</h3>
            <div className="executive-kpi-value executive-text-green-600">
              {kpiData.imoCompliance}% ✅
            </div>
            <p className="executive-text-sm executive-text-slate-600">
              All Emirates integrated under IMO FAL 2024 standard.
            </p>
          </div>

          <div className="executive-kpi-item">
            <h3 className="executive-kpi-label">Active Emirates</h3>
            <div className="executive-kpi-value executive-text-amber-600">
              {kpiData.activeEmirates} / 7
            </div>
            <p className="executive-text-sm executive-text-slate-600">
              Each Emirate connected to NMC via secure API gateway.
            </p>
          </div>

          <div className="executive-kpi-item">
            <h3 className="executive-kpi-label">Data Sync Rate</h3>
            <div className="executive-kpi-value executive-text-indigo-600">
              {kpiData.dataSyncRate}%
            </div>
            <p className="executive-text-sm executive-text-slate-600">
              Average percentage of real-time data synchronization with ports.
            </p>
          </div>

          <div className="executive-kpi-item">
            <h3 className="executive-kpi-label">System Uptime</h3>
            <div className="executive-kpi-value executive-text-emerald-600">
              {kpiData.systemUptime}%
            </div>
            <p className="executive-text-sm executive-text-slate-600">
              Overall operational uptime for national maritime servers.
            </p>
          </div>
        </div>

        {/* Charts Section */}
        <div className="executive-charts-grid">
          <ExecutiveCard>
            <ExecutiveCardHeader>
              <ExecutiveCardTitle>
                <div className="executive-icon-badge">
                  <TrendingUp size={20} />
                </div>
                Monthly Revenue Trend (AED Millions)
              </ExecutiveCardTitle>
            </ExecutiveCardHeader>
            <ExecutiveCardContent>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={revenueTrend}>
                  <XAxis dataKey="month" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#1d4ed8"
                    strokeWidth={3}
                    dot={{ fill: "#1d4ed8", strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, fill: "#1e40af" }}
                  />
                </LineChart>
              </ResponsiveContainer>
              <p className="executive-text-sm executive-text-slate-600 executive-mt-3">
                Trend shows steady growth across all Emirates driven by
                increased automation and digital port fee collections.
              </p>
            </ExecutiveCardContent>
          </ExecutiveCard>

          <ExecutiveCard>
            <ExecutiveCardHeader>
              <ExecutiveCardTitle>
                <div className="executive-icon-badge">
                  <Target size={20} />
                </div>
                Revenue Distribution (PPP Model)
              </ExecutiveCardTitle>
            </ExecutiveCardHeader>
            <ExecutiveCardContent>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={distributionData}
                    dataKey="value"
                    outerRadius={100}
                    innerRadius={60}
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {distributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <p className="executive-text-sm executive-text-slate-600 executive-mt-3">
                Revenue split under PPP: 25% CEBS (System Operations), 25% MOEI
                (Regulation), 50% Ports (Operations).
              </p>
            </ExecutiveCardContent>
          </ExecutiveCard>
        </div>

        {/* Insights Section */}
        <div className="executive-insights-grid">
          <ExecutiveCard>
            <ExecutiveCardHeader>
              <ExecutiveCardTitle>
                <div className="executive-icon-badge">
                  <BarChart3 size={20} />
                </div>
                Port-Wise Revenue Breakdown (AED Millions)
              </ExecutiveCardTitle>
            </ExecutiveCardHeader>
            <ExecutiveCardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={revenueByPort}>
                  <XAxis dataKey="port" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                  <Bar dataKey="value" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <p className="executive-text-sm executive-text-slate-600 executive-mt-3">
                Jebel Ali and Khalifa Ports contribute over 70% of total
                maritime revenue. Smaller ports show consistent digital adoption
                growth.
              </p>
            </ExecutiveCardContent>
          </ExecutiveCard>

          <ExecutiveCard>
            <ExecutiveCardHeader>
              <ExecutiveCardTitle>
                <div className="executive-icon-badge">
                  <Brain size={20} />
                </div>
                AI Forecasts & Operational Insights
              </ExecutiveCardTitle>
            </ExecutiveCardHeader>
            <ExecutiveCardContent>
              <ul className="executive-ai-insights">
                {aiInsights.map((item, i) => (
                  <li key={i} className="executive-ai-insight-item">
                    {item}
                  </li>
                ))}
              </ul>
              <p className="executive-text-sm executive-text-slate-600 executive-mt-3">
                AI algorithms analyze traffic patterns, port efficiency, and FAL
                submissions to predict upcoming operational and revenue trends.
              </p>
            </ExecutiveCardContent>
          </ExecutiveCard>
        </div>

        {/* Integration Section */}
        <div className="executive-integration-grid">
          <ExecutiveCard>
            <ExecutiveCardHeader>
              <ExecutiveCardTitle>
                <div className="executive-icon-badge">
                  <MapPin size={20} />
                </div>
                UAE Integration & Compliance Map
              </ExecutiveCardTitle>
            </ExecutiveCardHeader>
            <ExecutiveCardContent>
              <p className="executive-text-slate-600 executive-mb-4 executive-leading-relaxed">
                All seven Emirates are connected through NMC's national maritime
                data exchange hub, with API-based live data feeds. Status:{" "}
                <strong>100% IMO FAL compliance</strong> across all ports and
                terminals.
              </p>
              <ul className="executive-status-list">
                {emirateStatus.map((emirate, index) => (
                  <li key={index} className="executive-status-item">
                    <div
                      className="executive-status-indicator"
                      style={{ backgroundColor: emirate.color }}
                    ></div>
                    <div className="executive-flex executive-justify-between executive-items-center executive-w-full">
                      <span className="executive-font-semibold executive-text-slate-800">
                        {emirate.name}
                      </span>
                      <span className="executive-text-sm executive-text-green-600 executive-font-medium">
                        {emirate.sync}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </ExecutiveCardContent>
          </ExecutiveCard>

          <ExecutiveCard>
            <ExecutiveCardHeader>
              <ExecutiveCardTitle>
                <div className="executive-icon-badge">
                  <Database size={20} />
                </div>
                System Performance Metrics
              </ExecutiveCardTitle>
            </ExecutiveCardHeader>
            <ExecutiveCardContent>
              <ul className="executive-system-metrics">
                {systemMetrics.map((metric, index) => (
                  <li key={index} className="executive-system-metric">
                    <span className="executive-text-slate-700">
                      {metric.metric}
                    </span>
                    <span className="executive-metric-value">
                      {metric.value}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="executive-text-sm executive-text-slate-600 executive-mt-3">
                NMC's infrastructure runs on UAE sovereign cloud, ensuring high
                availability, redundancy, and security with 99.95% uptime.
              </p>
            </ExecutiveCardContent>
          </ExecutiveCard>
        </div>
      </div>
    </MaritimeLayout>
  );
}
